import { html, joinHtml } from "./html.js";
import { L } from "./vendor.js";
import { ui } from "./state.js";
import { convertUnitValue, unitPreference } from "./units.js";
import {
  TEMPERATURE_COLOR_STOPS, currentDirectionLabel, currentProfileDepthLabel, currentSpeedLabel,
  greatLakesControlValue, greatLakesHistoryMs, greatLakesModelsForView, greatLakesTimeParams, historyTimeLabel,
  paletteColor, waterTemperatureLabel, waveDirectionText, waveHeightLabel
} from "./great-lakes-conditions.js";
import { directionIconHtml, readingHtml } from "./cards.js";
import { greatLakesAnimationActive } from "./great-lakes-animation.js";
import { TEMPERATURE_COLOR_RANGE_C } from "./great-lakes-palette.js";
import { formatAppDate, formatAppTime } from "./app-time.js";

// Where Great Lakes values come from: live NOAA buoy/shore measurements, and
// the forecast model's own calculation points (FVCOM mesh nodes for
// temperature, triangle centres for currents).
export const STATIONS_STORAGE_KEY = "glc.Stations";
export const MODEL_POINTS_STORAGE_KEY = "glc.ModelPoints";
export const STATIONS_REFRESH_MS = 10 * 60 * 1000;

let map = null;
let stationsLayer = null;
let stationMarkers = [];
let modelPointsLayer = null;
let modelPointsRenderer = null;
let stationsPayload = null;
let stationsRefreshTimer = null;
let stationsRequest = null;
let modelPointsRequest = null;
let modelPointsTimer = null;
let layerRange = null;
let stationsMessage = "";
let modelPointsMessage = "";

function stored(key) {
  try { return localStorage.getItem(key) === "1"; } catch { return false; }
}

function store(key, enabled) {
  try { localStorage.setItem(key, enabled ? "1" : "0"); } catch { /* storage unavailable */ }
}

function toggle(name) {
  return document.querySelector(`[data-gl-${name}]`);
}

function setPointsStatus() {
  const node = document.querySelector("[data-gl-points-status]");
  if (node) node.textContent = [stationsMessage, modelPointsMessage].filter(Boolean).join("\n");
}

export function relativeTimeLabel(isoTime, now = Date.now()) {
  const time = Date.parse(isoTime);
  if (Number.isNaN(time)) return "at an unknown time";
  const minutes = Math.max(0, Math.round((now - time) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60), remainder = minutes % 60;
  return remainder ? `${hours} h ${remainder} min ago` : `${hours} h ago`;
}

export function shortTemperatureLabel(temperatureC) {
  const unit = unitPreference("waterTemperature");
  const value = Number(convertUnitValue(temperatureC, "C", unit));
  return `${Math.round(value)}°`;
}

// Stations share the temperature overlay's colour range while it shows the
// surface, so a buoy and the water around it read the same colour.
export function stationTemperatureRange() {
  return { minC: TEMPERATURE_COLOR_RANGE_C[0], maxC: TEMPERATURE_COLOR_RANGE_C[1] };
}

export function stationMarkerHtml(station, range) {
  const temperature = station.waterTemperature?.temperatureC;
  const current = (station.current?.values || [])[0];
  const hasTemperature = Number.isFinite(temperature);
  const color = hasTemperature ? paletteColor((temperature - range.minC) / (range.maxC - range.minC), TEMPERATURE_COLOR_STOPS) : "rgb(148, 163, 184)";
  return html`<span class="great-lakes-station${current ? " has-current" : ""}" style="--station-color: ${color}">
    <i class="great-lakes-station-dot" aria-hidden="true"></i>${hasTemperature ? html`<b>${shortTemperatureLabel(temperature)}</b>` : ""}${current ? html`<svg class="great-lakes-station-arrow" viewBox="0 0 24 24" aria-hidden="true" style="transform:rotate(${Math.round(current.directionDegrees)}deg)"><path d="M12 2.5 19 20.5 12 16.2 5 20.5Z"/></svg>` : ""}
  </span>`;
}

export function stationPopupHtml(station, now = Date.now(), historyHtml = null) {
  const temperature = station.waterTemperature;
  const current = station.current;
  const details = [station.type, station.owner, station.id].filter(Boolean).join(" · ");
  const temperatureBlock = temperature ? readingHtml({
    label: "Water temperature",
    value: waterTemperatureLabel(temperature.temperatureC),
    note: `Measured ${relativeTimeLabel(temperature.observedAt, now)}`
  }) : "";
  const waves = station.waves;
  const waveDetails = waves ? [
    Number.isFinite(waves.periodSeconds) ? `${Math.round(waves.periodSeconds)} s period` : "",
    Number.isFinite(waves.directionDegrees) ? waveDirectionText(waves.directionDegrees) : ""
  ].filter(Boolean).join(" · ") : "";
  const wavesBlock = waves ? readingHtml({
    label: "Waves",
    value: waveHeightLabel(waves.heightMeters),
    note: `${waveDetails ? `${waveDetails} · ` : ""}Measured ${relativeTimeLabel(waves.observedAt, now)}`
  }) : "";
  const currentRows = current ? joinHtml(current.values.map((value) => {
    const still = value.speedMetersPerSecond < 0.001;
    const direction = still ? "Still" : `Toward ${currentDirectionLabel(value.directionDegrees)} · ${Math.round(value.directionDegrees) % 360}°`;
    return html`<div role="listitem"><span>${currentProfileDepthLabel(value.depthMeters)}</span><strong>${currentSpeedLabel(value.speedMetersPerSecond)}</strong><span>${still ? "" : directionIconHtml(value.directionDegrees)}${direction}</span></div>`;
  }), "") : "";
  const currentBlock = current ? html`<section class="gl-reading">
    <span class="gl-reading-label">Current by depth</span>
    <div class="gl-current-table" role="list">${currentRows}</div>
    <span class="gl-reading-note">Measured ${relativeTimeLabel(current.observedAt, now)}</span>
  </section>` : "";
  return html`<article class="gl-card">
    <header class="gl-card-head"><strong class="gl-card-title">${station.name}</strong><span class="gl-card-sub">${details}</span></header>
    ${temperatureBlock}${wavesBlock}${currentBlock}
    <section class="gl-station-history" data-gl-station-history>${historyHtml || html`<span class="gl-reading-label">Past 90 days</span><p class="gl-reading-note">Loading saved readings…</p>`}</section>
    <a class="gl-card-link" href="${station.url}" target="_blank" rel="noopener noreferrer">NOAA station page<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3h7v7M13 3 4 12"/></svg></a>
  </article>`;
}

// A gap longer than this between saved readings breaks the line instead of joining across it.
export const STATION_HISTORY_GAP_MS = 9 * 3600 * 1000;
const SPARK_WIDTH = 248;
const SPARK_HEIGHT = 52;

function shortDate(milliseconds) {
  return formatAppDate(milliseconds);
}

// A small line chart of one station reading over the saved days; `points` are [milliseconds, value], oldest first.
export function stationSparklineHtml({ label, points, format, selected = null }) {
  const usable = (points || []).filter(([time, value]) => Number.isFinite(time) && Number.isFinite(value));
  if (usable.length < 2) return "";
  const first = usable[0][0], last = usable[usable.length - 1][0];
  const values = usable.map(([, value]) => value);
  let low = Math.min(...values), high = Math.max(...values);
  if (high - low < 1e-6) { low -= 0.5; high += 0.5; }
  const span = Math.max(1, last - first), pad = 3;
  const x = (time) => ((time - first) / span * SPARK_WIDTH).toFixed(1);
  const y = (value) => (pad + (1 - (value - low) / (high - low)) * (SPARK_HEIGHT - 2 * pad)).toFixed(1);
  let path = "";
  usable.forEach(([time, value], index) => {
    const joined = index > 0 && time - usable[index - 1][0] <= STATION_HISTORY_GAP_MS;
    path += `${joined ? "L" : "M"}${x(time)} ${y(value)}`;
  });
  const marker = Number.isFinite(selected) && selected >= first && selected <= last
    ? html`<line class="gl-spark-marker" x1="${x(selected)}" x2="${x(selected)}" y1="0" y2="${SPARK_HEIGHT}"/>` : "";
  const latest = usable[usable.length - 1][1];
  return html`<div class="gl-spark">
    <div class="gl-spark-head"><span>${label}</span><strong>${format(latest)}</strong></div>
    <svg viewBox="0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}" preserveAspectRatio="none" role="img" aria-label="${label} from ${shortDate(first)} to ${shortDate(last)}: ${format(low)} to ${format(high)}">${marker}<path d="${path}"/></svg>
    <div class="gl-spark-axis"><span>${shortDate(first)}</span><span>${format(low)} – ${format(high)}</span><span>${shortDate(last)}</span></div>
  </div>`;
}

// The station's saved readings (``/history/stations/<id>``) as small charts.
export function stationHistoryHtml(history, selected = null) {
  const series = (entries, pick) => (entries || []).map((entry) => [entry[0] * 1000, pick(entry)]);
  const charts = [
    stationSparklineHtml({ label: "Water temperature", points: series(history?.temperature, (entry) => entry[1]), format: waterTemperatureLabel, selected }),
    stationSparklineHtml({ label: "Wave height", points: series(history?.waves, (entry) => entry[1]), format: waveHeightLabel, selected }),
    stationSparklineHtml({ label: "Current (top reading)", points: series(history?.current, (entry) => entry[1]?.[1]), format: currentSpeedLabel, selected })
  ].filter((chart) => String(chart));
  if (!charts.length) return html`<span class="gl-reading-label">Past ${history?.days || 90} days</span><p class="gl-reading-note">Not enough saved readings yet.</p>`;
  return html`<span class="gl-reading-label">Past ${history?.days || 90} days</span>${charts}`;
}

// Saved readings per station, kept a few minutes: the popup's content is rebuilt from it when it opens.
const stationHistories = new Map();
const STATION_HISTORY_MAX_AGE_MS = 10 * 60 * 1000;

function savedStationHistoryHtml(station) {
  const saved = stationHistories.get(station.id);
  if (!saved) return null;
  if ("error" in saved) {
    return html`<span class="gl-reading-label">Past 90 days</span><p class="gl-reading-note">${saved.error === 404 ? "No saved readings for this station yet." : "Saved readings are unavailable right now."}</p>`;
  }
  return stationHistoryHtml(saved.history, greatLakesTimeParams().time ? greatLakesHistoryMs : null);
}

async function loadStationHistory(station, popup) {
  const saved = stationHistories.get(station.id);
  if (saved && (saved.loading || Date.now() - saved.at < STATION_HISTORY_MAX_AGE_MS)) return;
  stationHistories.set(station.id, { loading: true, at: Date.now() });
  try {
    stationHistories.set(station.id, { history: await window.noaaGreatLakesApi.stationHistory({ id: station.id }), at: Date.now() });
  } catch (error) {
    // An error is retried the next time the popup opens.
    stationHistories.set(station.id, { error: /\(404\)/.test(error.message) ? 404 : 503, at: 0 });
  }
  if (popup.isOpen()) popup.update();
}

export function overlapsAny(box, boxes) {
  return boxes.some((other) => box.left < other.right && box.right > other.left && box.top < other.bottom && box.bottom > other.top);
}

// Greedy label placement: a label that would overlap one already placed
// collapses to its coloured dot, which stays clickable.
function declutterStations() {
  if (!map) return;
  const placed = [];
  const labels = stationMarkers.flatMap((marker) => {
    const element = marker.getElement()?.querySelector(".great-lakes-station");
    return element ? [{ element, marker }] : [];
  });
  labels.forEach(({ element }) => element.classList.remove("is-compact"));
  // Measure all full labels before changing their compact styles.
  const measured = labels.map(({ element, marker }) => ({
    element,
    point: map.latLngToContainerPoint(marker.getLatLng()),
    width: element.offsetWidth || 48,
    height: element.offsetHeight || 20
  }));
  measured.forEach(({ element, point, width, height }) => {
    const box = { left: point.x - 10, right: point.x - 10 + width, top: point.y - height / 2, bottom: point.y + height / 2 };
    if (overlapsAny(box, placed)) {
      element.classList.add("is-compact");
      placed.push({ left: point.x - 8, right: point.x + 8, top: point.y - 8, bottom: point.y + 8 });
    } else {
      placed.push(box);
    }
  });
}

function latestStationObservationTime(stations) {
  let latest = null;
  for (const station of stations) {
    for (const reading of [station.waterTemperature, station.current, station.waves]) {
      const timestamp = Date.parse(reading?.observedAt || "");
      if (Number.isFinite(timestamp) && (latest === null || timestamp > latest)) latest = timestamp;
    }
  }
  return latest;
}

function renderStations() {
  if (!stationsLayer) return;
  stationsLayer.clearLayers();
  stationMarkers = [];
  if (greatLakesAnimationActive() || !toggle("stations")?.checked || !stationsPayload) return;
  const stations = stationsPayload.stations || [];
  const range = stationTemperatureRange(stations, layerRange);
  // Current meters and offshore buoys keep their labels first when crowded.
  const priority = (station) => (station.current ? 0 : station.type === "Buoy" ? 1 : 2);
  [...stations].sort((first, second) => priority(first) - priority(second)).forEach((station) => {
    const icon = L.divIcon({ className: "great-lakes-station-marker", iconSize: [0, 0], iconAnchor: [0, 0], html: String(stationMarkerHtml(station, range)) });
    const marker = L.marker([station.latitude, station.longitude], { icon, pane: "greatLakesStations", title: station.name, alt: station.name, riseOnHover: true })
      .bindPopup(() => String(stationPopupHtml(station, Date.now(), savedStationHistoryHtml(station))), { className: "gl-popup", minWidth: 240, maxWidth: 290 })
      .on("popupopen", (event) => loadStationHistory(station, event.popup))
      .addTo(stationsLayer);
    stationMarkers.push(marker);
  });
  declutterStations();
  const withCurrents = stations.filter((station) => station.current).length;
  const past = stationsPayload.historyTime;
  const latestObservation = latestStationObservationTime(stations);
  stationsMessage = past
    ? (stations.length ? `${stations.length} buoys with saved readings for ${historyTimeLabel(past)}${withCurrents ? `, ${withCurrents} with current meters` : ""}.` : `No saved buoy readings for ${historyTimeLabel(past)}.`)
    : stations.length
      ? `${stations.length} buoys reporting${withCurrents ? `, ${withCurrents} with current meters` : ""}. ${latestObservation === null ? "NOAA reading time unavailable." : `Latest NOAA reading at ${formatAppTime(latestObservation)}.`}`
      : "No buoys are reporting right now.";
  setPointsStatus();
}

async function loadStations() {
  if (!toggle("stations")?.checked) return;
  stationsRequest?.abort();
  stationsRequest = new AbortController();
  stationsMessage = "Loading NOAA buoys…";
  setPointsStatus();
  try {
    stationsPayload = await window.noaaGreatLakesApi.observations({ signal: stationsRequest.signal, ...greatLakesTimeParams() });
    renderStations();
  } catch (error) {
    if (error.name === "AbortError") return;
    stationsMessage = "NOAA buoys are unavailable right now.";
    setPointsStatus();
  }
}

function setStationsEnabled(enabled) {
  store(STATIONS_STORAGE_KEY, enabled);
  clearInterval(stationsRefreshTimer);
  if (!enabled) {
    stationsRequest?.abort();
    stationsLayer?.clearLayers();
    stationsMessage = "";
    setPointsStatus();
    return;
  }
  loadStations();
  stationsRefreshTimer = setInterval(loadStations, STATIONS_REFRESH_MS);
}

export function modelPointKind() {
  const layer = greatLakesControlValue("layer");
  return ["currents", "waves", "wind"].includes(layer) ? layer : "temperature";
}

const MODEL_POINT_LABELS = {
  wind: "wind calculation points (0.25° GFS grid)",
  currents: "current calculation points (model triangle centres)",
  waves: "wave calculation points (2.5 km wave model grid)",
  temperature: "temperature calculation points (model mesh nodes)"
};

async function loadModelPoints() {
  modelPointsRequest?.abort();
  modelPointsLayer?.clearLayers();
  if (!map || !toggle("model-points")?.checked) return;
  const models = greatLakesModelsForView(map);
  if (!models.length) {
    modelPointsMessage = "Move the map over a Great Lake to see model calculation points.";
    setPointsStatus();
    return;
  }
  const kind = modelPointKind();
  const bounds = map.getBounds().pad(0.05);
  modelPointsRequest = new AbortController();
  modelPointsMessage = "Loading model calculation points…";
  setPointsStatus();
  try {
    const payload = await window.noaaGreatLakesApi.modelPoints({
      kind, models: models.join(","), signal: modelPointsRequest.signal,
      south: Math.max(-90, bounds.getSouth()).toFixed(4), west: Math.max(-180, bounds.getWest()).toFixed(4),
      north: Math.min(90, bounds.getNorth()).toFixed(4), east: Math.min(180, bounds.getEast()).toFixed(4)
    });
    const label = MODEL_POINT_LABELS[kind];
    if (payload.tooMany) {
      modelPointsMessage = `Zoom in to see model points: ${payload.count.toLocaleString()} ${label} are in this view.`;
    } else {
      const points = payload.points || [];
      for (let index = 0; index < points.length; index += 2) {
        L.circleMarker([points[index], points[index + 1]], {
          renderer: modelPointsRenderer, interactive: false, radius: 2.4, weight: 1,
          color: "rgba(255, 255, 255, 0.92)", fillColor: "#0b1730", fillOpacity: 0.92
        }).addTo(modelPointsLayer);
      }
      modelPointsMessage = `${payload.count.toLocaleString()} ${label} in view.`;
    }
    setPointsStatus();
  } catch (error) {
    if (error.name === "AbortError") return;
    modelPointsMessage = "NOAA model calculation points are unavailable right now.";
    setPointsStatus();
  }
}

function scheduleModelPoints(delay = 220) {
  clearTimeout(modelPointsTimer);
  modelPointsTimer = setTimeout(loadModelPoints, delay);
}

function setModelPointsEnabled(enabled) {
  store(MODEL_POINTS_STORAGE_KEY, enabled);
  if (enabled) { scheduleModelPoints(0); return; }
  clearTimeout(modelPointsTimer);
  modelPointsRequest?.abort();
  modelPointsLayer?.clearLayers();
  modelPointsMessage = "";
  setPointsStatus();
}

function attach(leafletMap) {
  map = leafletMap;
  if (!map.getPane("greatLakesModelPoints")) {
    map.createPane("greatLakesModelPoints").style.zIndex = 450;
    map.getPane("greatLakesModelPoints").style.pointerEvents = "none";
  }
  if (!map.getPane("greatLakesStations")) map.createPane("greatLakesStations").style.zIndex = 590;
  modelPointsRenderer = L.canvas({ pane: "greatLakesModelPoints", padding: 0.1 });
  modelPointsLayer = L.layerGroup().addTo(map);
  stationsLayer = L.layerGroup().addTo(map);
  map.on("moveend", () => { if (toggle("model-points")?.checked) scheduleModelPoints(); });
  map.on("zoomend", declutterStations);
  const stationsToggle = toggle("stations"), modelPointsToggle = toggle("model-points");
  if (stationsToggle) stationsToggle.checked = stored(STATIONS_STORAGE_KEY);
  if (modelPointsToggle) modelPointsToggle.checked = stored(MODEL_POINTS_STORAGE_KEY);
  if (stationsToggle?.checked) setStationsEnabled(true);
  if (modelPointsToggle?.checked) setModelPointsEnabled(true);
}

export function setup() {
  document.addEventListener("change", (event) => {
    if (!map) return;
    if (event.target.matches("[data-gl-stations]")) setStationsEnabled(event.target.checked);
    else if (event.target.matches("[data-gl-model-points]")) setModelPointsEnabled(event.target.checked);
    else if (event.target.matches("[data-gl-layer]") && toggle("model-points")?.checked) scheduleModelPoints();
  });

  // Station dots use the colours on screen: the surface temperature range the map is fitted to.
  document.addEventListener("great-lakes-palette-range", (event) => {
    const { kind, minimum, maximum, depth } = event.detail || {};
    if (kind !== "temperature" || depth) return;
    layerRange = { minC: minimum, maxC: maximum };
    renderStations();
  });

  // A past sample on the map: stations show what they measured then.
  let shownTime = null;
  document.addEventListener("great-lakes-history-time", (event) => {
    const time = event.detail?.time || null;
    if (time === shownTime) return;
    shownTime = time;
    loadStations();
  });
  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-gl-forecast]") && shownTime && !greatLakesTimeParams().time) {
      shownTime = null;
      loadStations();
    }
  });

  document.addEventListener("great-lakes-layer-loaded", (event) => {
    const { temperatureMetadata, depth } = event.detail || {};
    layerRange = temperatureMetadata && !depth && Number.isFinite(Number(temperatureMetadata.minC))
      ? { minC: Number(temperatureMetadata.minC), maxC: Number(temperatureMetadata.maxC) }
      : null;
    renderStations();
  });

  let animationActive = greatLakesAnimationActive();
  document.addEventListener("great-lakes-animation-state", (event) => {
    if (animationActive === event.detail.active) return;
    animationActive = event.detail.active;
    renderStations();
  });

  let attempts = 0;
  const attachWhenMapReady = setInterval(() => {
    attempts += 1;
    if (ui.map && document.querySelector("[data-gl-stations]")) {
      clearInterval(attachWhenMapReady);
      attach(ui.map);
    } else if (attempts > 600) {
      clearInterval(attachWhenMapReady);
    }
  }, 50);
}
